import { Centrifuge } from "centrifuge";
import { getConfig } from "@/lib/config";

let centrifuge: Centrifuge | null = null;
let onReconnectCallback: (() => void) | null = null;

export function setOnReconnect(cb: () => void) {
  onReconnectCallback = cb;
}

async function getConnectionToken(): Promise<string> {
  const { chat } = await import("@/lib/http");
  try {
    const data = await chat<{ token: string }>("/v1/chat/realtime/token");
    return data.token;
  } catch {
    // First attempt failed (JWT expired, network error).
    // chat() auto-refreshes on 401 — retry once after short delay.
    await new Promise((r) => setTimeout(r, 1000));
    try {
      const data = await chat<{ token: string }>("/v1/chat/realtime/token");
      return data.token;
    } catch (e) {
      console.warn("[centrifugo] failed to get connection token after retry", e);
      throw e;
    }
  }
}

export function getClient(): Centrifuge {
  if (!centrifuge) {
    centrifuge = new Centrifuge(getConfig().centrifugoUrl, {
      getToken: getConnectionToken,
    });
    let wasConnected = false;
    centrifuge.on("connected", (ctx) => {
      console.log("[centrifugo] connected", ctx);
      if (wasConnected && onReconnectCallback) {
        console.log("[centrifugo] reconnected — syncing state");
        onReconnectCallback();
      }
      wasConnected = true;
    });
    centrifuge.on("disconnected", (ctx) => {
      console.log("[centrifugo] disconnected", ctx);
    });
    centrifuge.on("error", (ctx) => {
      console.error("[centrifugo] error", ctx);
    });
  }
  return centrifuge;
}

export function connect() {
  const client = getClient();
  if (client.state !== "connected") {
    client.connect();
  }
}

export function disconnect() {
  if (centrifuge) {
    centrifuge.disconnect();
    centrifuge = null;
  }
}

export function subscribe(channel: string, onMessage: (data: unknown) => void) {
  const client = getClient();
  try {
    const existing = client.getSubscription(channel);
    if (existing) {
      existing.unsubscribe();
      client.removeSubscription(existing);
    }
  } catch {
    // no existing subscription
  }
  const sub = client.newSubscription(channel);
  sub.on("publication", (ctx) => {
    console.log("[centrifugo] message on", channel, ctx.data);
    onMessage(ctx.data);
  });
  sub.on("subscribed", (ctx) => {
    console.log("[centrifugo] subscribed to", channel);
  });
  sub.on("error", (ctx) => {
    console.error("[centrifugo] subscription error", channel, ctx);
  });
  sub.subscribe();
  return sub;
}
